/**
 * MOSA-364. Keep the pasted sheet link across sign-up, sign-in and the Google
 * consent redirect, so the user never pastes it twice.
 *
 * Two stores on purpose:
 *   - sessionStorage is the normal one: it covers a redirect that leaves the
 *     app and comes back in the same tab (the OAuth callback does).
 *   - localStorage is the fallback for a sign-up that opens a new tab, and for
 *     a browser that clears session data on a cross-site return.
 *
 * Only a link is stored: no token, no sheet contents, nothing about the
 * account. The value is the text the user pasted, unchanged.
 */

export const SHEETS_LINK_KEY = "od:sheetsConnectLink";

/** Same-tab state for the run of the wizard, kept in memory when possible. */
let memory: string | null | undefined;

function safeGet(store: Storage | undefined, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(store: Storage | undefined, key: string, value: string) {
  try {
    store?.setItem(key, value);
  } catch {
    /* private mode, quota */
  }
}

function safeRemove(store: Storage | undefined, key: string) {
  try {
    store?.removeItem(key);
  } catch {
    /* ignore */
  }
}

const session = (): Storage | undefined =>
  typeof window === "undefined" ? undefined : window.sessionStorage;
const local = (): Storage | undefined =>
  typeof window === "undefined" ? undefined : window.localStorage;

export function saveSheetsLink(link: string): void {
  const value = link.trim();
  if (!value) {
    clearSheetsLink();
    return;
  }
  memory = value;
  safeSet(session(), SHEETS_LINK_KEY, value);
  safeSet(local(), SHEETS_LINK_KEY, value);
}

/**
 * The saved link, or null. When the two stores disagree, the newer write is in
 * both, so either order is fine; session wins because it is the tab we are in.
 */
export function loadSheetsLink(): string | null {
  if (memory !== undefined) {
    return memory;
  }
  return (
    safeGet(session(), SHEETS_LINK_KEY) ?? safeGet(local(), SHEETS_LINK_KEY)
  );
}

export function clearSheetsLink(): void {
  memory = null;
  safeRemove(session(), SHEETS_LINK_KEY);
  safeRemove(local(), SHEETS_LINK_KEY);
}

/** Tests only: drop the in-memory copy so the stores are read again. */
export function __resetSheetsLinkMemory(): void {
  memory = undefined;
}
